import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Shield, Users, Smartphone, Bot, Bell, CreditCard, Calendar, Lock, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if user is logged in (came from dashboard)
  const isLoggedIn = !!user;

  const features = [
    {
      icon: Shield,
      title: 'Smart Visitor Management',
      description: 'Pre-approve visitors, track entries, and manage gate passes digitally',
      image: 'https://images.pexels.com/photos/17649450/pexels-photo-17649450.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      icon: Bot,
      title: 'AI Assistant',
      description: '24/7 intelligent chatbot for instant answers to society queries',
      image: 'https://images.unsplash.com/photo-1758687126877-b37052a20a4d?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      icon: Bell,
      title: 'Real-time Notifications',
      description: 'Stay updated with announcements, alerts, and community news',
      image: 'https://images.unsplash.com/photo-1707508093903-6604b40b15de?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      icon: CreditCard,
      title: 'Digital Billing',
      description: 'Manage maintenance payments and track billing history seamlessly',
      image: 'https://images.unsplash.com/photo-1758194403577-a9e4dbfbade1?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      icon: Calendar,
      title: 'Amenity Booking',
      description: 'Book gym, pool, clubhouse, and courts with ease',
      image: 'https://images.unsplash.com/photo-1630703178161-1e2f9beddbf8?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      icon: Users,
      title: 'Community Hub',
      description: 'Connect with neighbors, raise complaints, and build community',
      image: 'https://images.pexels.com/photos/29852895/pexels-photo-29852895.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/80 backdrop-blur-xl border-b border-slate-200/50 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-primary rounded-xl flex items-center justify-center">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-primary" style={{ fontFamily: 'Manrope' }}>FortiHome</span>
          </div>
          <nav className="hidden md:flex gap-8 items-center">
            <a href="#features" className="text-slate-600 hover:text-primary transition-colors">Features</a>
            <a href="#about" className="text-slate-600 hover:text-primary transition-colors">About</a>
            {isLoggedIn ? (
              <Button 
                onClick={() => navigate(`/${user.role}`)} 
                className="bg-secondary hover:bg-secondary/90"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => navigate('/login')} data-testid="header-login-btn">Login</Button>
                <Button onClick={() => navigate('/register')} className="bg-secondary hover:bg-secondary/90" data-testid="header-register-btn">Get Started</Button>
              </>
            )}
          </nav>
          {/* Mobile menu */}
          <div className="md:hidden">
            {isLoggedIn ? (
              <Button 
                size="sm"
                onClick={() => navigate(`/${user.role}`)} 
                className="bg-secondary hover:bg-secondary/90"
                data-testid="mobile-back-to-dashboard-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Dashboard
              </Button>
            ) : (
              <Button size="sm" onClick={() => navigate('/login')} data-testid="mobile-login-btn">Login</Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section - Different for logged-in users */}
      {isLoggedIn ? (
        <section className="pt-32 pb-12 px-6">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight" style={{ fontFamily: 'Manrope' }}>
                Welcome back, {user.name}!
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                You're logged in as <span className="font-semibold capitalize">{user.role}</span>. 
                Explore our features below or return to your dashboard.
              </p>
              <Button 
                size="lg" 
                className="bg-secondary hover:bg-secondary/90 text-white px-8 py-6 text-lg" 
                onClick={() => navigate(`/${user.role}`)}
                data-testid="hero-back-to-dashboard-btn"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Dashboard
              </Button>
            </motion.div>
          </div>
        </section>
      ) : (
        <section className="pt-32 pb-20 px-6 landing-hero">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight" style={{ fontFamily: 'Manrope' }}>
                Smart Living for Modern Communities
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-10 leading-relaxed max-w-2xl mx-auto">
                Experience the future of residential society management with AI-powered assistance, 
                seamless security, and community engagement all in one platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white px-8 py-6 text-lg" onClick={() => navigate('/register')} data-testid="hero-get-started-btn">
                  Get Started Free
                </Button>
                <Button size="lg" variant="outline" className="px-8 py-6 text-lg" onClick={() => navigate('/visitor')} data-testid="hero-visitor-portal-btn">
                  Visitor Portal
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-16 relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1758194403577-a9e4dbfbade1?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="FortiHome Dashboard"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Features Grid */}
      <section id="features" className="py-20 px-6">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>Everything You Need</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Comprehensive features designed for seamless society management</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="feature-card p-6 h-full border-slate-200 bg-white shadow-sm hover:shadow-xl">
                    <div className="h-48 mb-4 rounded-xl overflow-hidden">
                      <img src={feature.image} alt={feature.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="h-12 w-12 bg-secondary/10 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-secondary" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>{feature.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-12 md:py-20 px-4 md:px-6 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>About FortiHome</h2>
              <p className="text-lg text-slate-600">Transforming residential living through smart technology</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>Our Mission</h3>
                  <p className="text-slate-600 leading-relaxed">
                    FortiHome is dedicated to revolutionizing residential society management by providing an intelligent, 
                    user-friendly platform that brings communities together. We believe in making everyday living simpler, 
                    safer, and more connected through innovative technology.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>Why Choose Us</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="h-6 w-6 bg-secondary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-slate-600">24/7 AI-powered assistance for instant support</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="h-6 w-6 bg-secondary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-slate-600">Seamless visitor management with digital gate passes</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="h-6 w-6 bg-secondary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-slate-600">Real-time notifications and updates</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="h-6 w-6 bg-secondary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-slate-600">Secure, encrypted data protection</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>What We Offer</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    FortiHome provides a comprehensive suite of tools designed specifically for modern residential communities:
                  </p>
                  <div className="space-y-3">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-semibold text-slate-900 mb-1">For Residents</h4>
                      <p className="text-sm text-slate-600">Easy amenity booking, complaint tracking, digital payments, and AI assistance</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-semibold text-slate-900 mb-1">For Security</h4>
                      <p className="text-sm text-slate-600">Streamlined visitor management, automated check-ins, and real-time monitoring</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-semibold text-slate-900 mb-1">For Management</h4>
                      <p className="text-sm text-slate-600">Analytics dashboard, bulk billing, amenity management, and community insights</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-secondary/10 to-accent/10 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>Join Hundreds of Communities</h3>
              <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
                Trusted by residential societies across the country. Experience the future of community living today.
              </p>
              {isLoggedIn ? (
                <Button size="lg" className="bg-secondary hover:bg-secondary/90" onClick={() => navigate(`/${user.role}`)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </Button>
              ) : (
                <Button size="lg" className="bg-secondary hover:bg-secondary/90" onClick={() => navigate('/register')}>
                  Get Started Now
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Only for non-logged-in users */}
      {!isLoggedIn && (
        <section className="py-20 px-6 bg-primary text-white">
          <div className="container mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Manrope' }}>Ready to Transform Your Community?</h2>
            <p className="text-xl mb-10 opacity-90 max-w-2xl mx-auto">Join hundreds of societies already using FortiHome</p>
            <Button size="lg" className="bg-white text-primary hover:bg-slate-100 px-8 py-6 text-lg" onClick={() => navigate('/register')} data-testid="cta-get-started-btn">
              Start Your Free Trial
            </Button>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-6">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-10 w-10 bg-secondary rounded-xl flex items-center justify-center">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>FortiHome</span>
          </div>
          <p className="text-slate-400 mb-4">Smart Living for Modern Communities</p>
          <p className="text-slate-500 text-sm">© 2026 FortiHome. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;